import { useEffect, useState, useRef } from 'react';
import AuthPage from './components/SignIn';
import HomePage from './components/HomePage';
import CreateQuiz from './components/CreateQuiz';
import QuizDetails from './components/QuizDetails';
import QuizInfoPage from './components/QuizInfoPage';
import PreviewQuiz from './components/PreviewQuiz';
import QuizPage from './components/QuizPage';
import LiveChallenge from './components/LiveChallenge';
import ChallengePage from './components/ChallengePage';
import SolveChallenge from './components/SolveChallenge';
import ChallengeResult from './components/ChallengeResult';
import ResultPage from './components/ResultPage';
import Chat from './components/Chat';
import copy from 'copy-to-clipboard';
import { LoaderContextProvider } from './contexts/loader';
import { PopUpContextProvider } from './contexts/Popup';
import { UserContextProvider } from './contexts/user';
import { QuizzesContextProvider } from "./contexts/Quizzes";
import { Routes, Route, useNavigate } from "react-router-dom";
import { get, onValue, ref, set, update } from 'firebase/database';
import { db } from './Database';
import { PreviewQuestionsProvider } from './contexts/Preview';
import { Copy, Github, InstagramIcon, Link2, Linkedin, Mail, WifiOff } from 'lucide-react';
import { ResultContextProvider } from './contexts/Results';
import { useCheckForNewToken } from './checkForNewToken';
import './App.css';

function App() {
  const navigate = useNavigate();

  const [messageContent, setMessageContent] = useState({ title: "", message: "", });
  const [user, setUser] = useState(localStorage.getItem("user") || null);
  const [userInfo, setUserInfo] = useState({});
  const [isLoading, setIsLoading] = useState(0);
  const [quizzes, setQuizzes] = useState([]);
  const [connected, setConnected] = useState(true);
  const [userIndex, setUserIndex] = useState(-1);
  const [userResult, setUserResult] = useState({});
  const [copyLink, setCopyLink] = useState("");
  const [previewQuestions, setPreviewQuestions] = useState(localStorage.getItem("questions") ? JSON.parse(localStorage.getItem("questions")) : []);
  const [userJWT, setUserJWT] = useState(localStorage.getItem("user token") || null);
  const [supervisorStudent, setSupervisorStudent] = useState({});
  const [appLanguage, setAppLanguage] = useState(localStorage.getItem("app language") || "en");

  const isArabic = appLanguage === "ar";
  const currentYear = new Date().getFullYear();

  const { checkForNewToken } = useCheckForNewToken();

  const popUpRef = useRef();

  useEffect(() => {
    if (user && !userInfo.id) {
      setIsLoading(prev => prev + 1);
      fetch("https://quiz-battle-api.vercel.app/api/user-data" , {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + userJWT,
        },
      })
        .then(res => res.json())
        .then(data => {
          checkForNewToken(data, setUserJWT, setUserInfo, setUser, setUserIndex, navigate, setMessageContent);
          const currentUser = data.userData;
          if (currentUser) {
            setUserIndex(1);
            setUserInfo(currentUser);
            setUser(currentUser.id);
            if (currentUser.role === "supervisor") {
              if (window.location.pathname !== "/" && window.location.pathname !== "/home") {
                navigate("/home");
              }
              setSupervisorStudent(currentUser.superVisorData);
              if (currentUser.superVisor.includes("waiting")) {
                setSupervisorStudent({ waiting: true, })
              } else if (currentUser.superVisor.includes("rejected")) {
                setSupervisorStudent({ rejected: true, })
              }
            } else {
              if (!currentUser.playingQuiz && !currentUser.challenge) {
                const windowPath = window.location.pathname;
                if (windowPath !== "/create" && windowPath !== "/" && windowPath !== "/preview" && !windowPath.includes("/details/") && !windowPath.includes("/info/") && !windowPath.includes("/quiz/") && !windowPath.includes("/result/") && !windowPath.includes("/chat/") && !windowPath.includes("/live-challenge/") && !windowPath.includes("/challenge/") && !windowPath.includes("/solving-challenge/") && !windowPath.includes("/challenge-result/")) {
                  navigate("/home");
                }
              } else {
                if (currentUser.challenge) {
                  if (!window.location.pathname.includes("/challenge/") && !window.location.pathname.includes("/solving-challenge/") && !window.location.pathname.includes("/challenge-result/")) {
                    navigate("/challenge/" + currentUser.challenge);
                  }
                } else {
                  navigate("/quiz/" + currentUser.playingQuiz);
                }
              }
            }
            setIsLoading(prev => prev !== 0 ? prev - 1 : prev);
          } else {
            localStorage.removeItem("user");
            setUser("");
            navigate("/sign-in")
            setIsLoading(prev => prev !== 0 ? prev - 1 : prev);
          }
        })
    } else {
      if (!user) {
        navigate("/sign-in");
      }
    }
  }, [user, navigate])


  useEffect(() => {
    if (userIndex !== -1 && quizzes.length == 0) {
      setIsLoading(prev => prev + 1);
      fetch("https://quiz-battle-api.vercel.app/api/quiz", {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${userJWT}`,
        },
      })
        .then(res => res.json())
        .then(data => {
          checkForNewToken(data, setUserJWT, setUserInfo, setUser, setUserIndex, navigate, setMessageContent);
          if (data.ok === 0) {
            setMessageContent({
              title: isArabic ? "فشل الجلب" : "Fetching Failed",
              message: isArabic ? "حدث خطأ أثناء جلب الاختبارات من قاعدة البيانات، أعد تحميل الصفحة للمحاولة مرة أخرى." : "Oops! something wrong occured while fetching the quizzes from the database, refresh the page to try again.",
            })
          } else {
            setQuizzes(data.quizzes ? data.quizzes : []);
          }
          setIsLoading(prev => prev !== 0 ? prev - 1 : prev);
        }).catch(err => {
          console.log(err);
          setIsLoading(prev => prev !== 0 ? prev - 1 : prev);
          setMessageContent({
            title: isArabic ? "فشل الجلب" : "Fetching Failed",
            message: isArabic ? "حدث خطأ أثناء جلب الاختبارات من قاعدة البيانات، أعد تحميل الصفحة للمحاولة مرة أخرى." : "Oops! something wrong occured while fetching the quizzes from the database, refresh the page to try again.",
          })
        })
    } else {
      setQuizzes([]);
    }
  }, [user, userIndex]);

  useEffect(() => {
    if(userIndex !== -1) {
      onValue(ref(db, "users/" + userInfo.id), (snapShot) => {
        setUserInfo(snapShot.val() || {});
      })
    }
  },[userIndex])

  useEffect(() => {
    if (navigator.onLine) {
      setConnected(true);
    } else {
      setConnected(false);
    }

    window.addEventListener("online", () => {
      setConnected(true);
    })

    window.addEventListener("offline", () => {
      setConnected(false);
    })

    window.addEventListener("beforeunload", () => {
      localStorage.setItem("user", user);
      localStorage.setItem("user token", userJWT);
    })

    window.addEventListener("keydown", (e) => {
      if (e.code === "F12") e.preventDefault();
    })

    window.addEventListener("contextmenu", (e) => e.preventDefault());
  }, [user])

  const hidePopUp = (e) => {
    if (popUpRef.current) {
      const popUpRect = popUpRef.current.getBoundingClientRect();
      const { clientX, clientY } = e;
      if (clientX > popUpRect.right || clientX < popUpRect.left || clientY > popUpRect.bottom || clientY < popUpRect.top)
        setMessageContent({ title: "", message: "", })
    }
  }

  return (
    <>
      <LoaderContextProvider value={{ isLoading, setIsLoading }}>
        <PopUpContextProvider value={{ setMessageContent, setCopyLink }}>
          <UserContextProvider value={{ setUser, user, setUserInfo, userInfo, userIndex, setUserIndex, setUserJWT, userJWT, supervisorStudent, setSupervisorStudent, appLanguage, setAppLanguage }}>
            <QuizzesContextProvider value={{ quizzes, setQuizzes }}>
              <PreviewQuestionsProvider value={{ previewQuestions, setPreviewQuestions }}>
                <ResultContextProvider value={{ userResult, setUserResult }}>
                  <Routes>
                    <Route path="/sign-in" element={<AuthPage />} />
                    <Route path="/" element={<><HomePage /></>} />
                    <Route path="/home" element={<HomePage />} />
                    <Route path="/create" element={<CreateQuiz />} />
                    <Route path="/preview" element={<PreviewQuiz />} />
                    <Route path="/details/:id" element={<QuizDetails />} />
                    <Route path="/info/:id" element={<QuizInfoPage />} />
                    <Route path="/quiz/:id" element={<QuizPage />} />
                    <Route path="/result/:id" element={<ResultPage />} />
                    <Route path="/chat/:id" element={<Chat />} />
                    <Route path="/live-challenge/:id" element={<LiveChallenge />} />
                    <Route path="/challenge/:id" element={<ChallengePage />} />
                    <Route path="/solving-challenge/:id" element={<SolveChallenge />} />
                    <Route path="/challenge-result/:id" element={<ChallengeResult />} />
                  </Routes>
                  {
                    window.location.pathname !== "/sign-in" && window.location.pathname !== "/preview" && !window.location.pathname.includes("/info/") && !window.location.pathname.includes("/quiz/") && !window.location.pathname.includes("/chat/") && !window.location.pathname.includes("/solving-challenge/") ?
                      <footer>
                        <div className="footer-container">
                          <div className="footer-top">
                            <div className="footer-left">
                              <h2>Quiz Battle</h2>
                              <p dir={isArabic ? "rtl" : "ltr"}>{isArabic ? "تم التطوير بواسطة" : "Developed by"} <strong>{isArabic ? "سيف ماهر السماحى" : "Saif Maher Elsmahy"}</strong></p>
                            </div>
                            <div className="footer-right">
                              <a text="LinkedIn" rel="noreferrer" className="linkedin" href="https://www.linkedin.com/in/sme-dev-5036a5350/" target="_blank"><Linkedin /></a>
                              <a text="Instagram" rel="noreferrer" className="instagram" href="https://instagram.com/sme.dev" target="_blank"><InstagramIcon /></a>
                              <a text="Github" rel="noreferrer" href="https://github.com/smeDeveloper" target="_blank"><Github /></a>
                              <a text="Email" rel="noreferrer" href="mailto:sme.dev212@gmail.com"><Mail /></a>
                            </div>
                          </div>
                          <div className="footer-bottom-content">
                            <div className="language">
                              <p>{isArabic ? "لغة التطبيق" : "App Language"}</p>
                              <select value={appLanguage} onChange={(e) => { localStorage.setItem("app language", e.target.value); setAppLanguage(e.target.value) }}>
                                <option value="en">English</option>
                                <option value="ar">عربى</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        <div className="footer-bottom">
                          <p>© <span id="year">{currentYear}&nbsp;</span>Quiz Battle. {isArabic ? "جميع الحقوق محفوظة." : "All rights reserved."}</p>
                        </div>
                      </footer> : undefined
                  }
                </ResultContextProvider>
              </PreviewQuestionsProvider>
            </QuizzesContextProvider>
          </UserContextProvider>
        </PopUpContextProvider>
      </LoaderContextProvider>
      {
        userInfo.blocked ? <div className="blocked_message">
          <div className="content">
            <div className="main_content">
              <h2>Access Blocked</h2>
              <p>You are blocked from using this website, so you can’t take any actions on it. Click the button below to leave.</p>
              <button onClick={() => window.history.back()}>Leave website</button>
            </div>
          </div>
        </div>
          : undefined
      }
      {
        isLoading ?
          <div className="loading_screen">
            <span></span>
          </div>
          : undefined
      }
      {
        copyLink ?
          <div className="quiz_link_container">
            <div className="content">
              <p className="title">{isArabic ? "رابط الاختبار" : "Quiz Link"}</p>
              <div className="main_content">
                <div className="link">
                  <div className="icon">
                    <Link2 />
                  </div>
                  <div className="link">{copyLink}</div>
                </div>
                <button onClick={() => {
                  copy(copyLink);
                  setTimeout(() => { copy(copyLink) }, 1);
                  setMessageContent({
                    title: isArabic ? "تم نسخ الرابط" : "Link Copied",
                    message: isArabic ? "تم نسخ رابط الاختبار! ✅" : "Quiz link has been copied! ✅",
                  })
                }}>{isArabic ? "نسخ" : "copy"} <Copy size={16} /></button>
                <button onClick={() => setCopyLink("")}>{isArabic ? "إغلاق" : "Close"}</button>
              </div>
            </div>
          </div> : undefined
      }
      {
        messageContent.message ?
          <div onClick={hidePopUp} className="pop_up_container">
            <div ref={popUpRef} className="content">
              <div className="pop_up_title">{messageContent.title}</div>
              <div className="pop_up_content">{messageContent.message}</div>
            </div>
          </div>
          : undefined
      }
      {
        !connected ?
          <div className="no_internet">
            <div className="content">
              <WifiOff size={70} />
              <p>{isArabic ? "لا يوجد اتصال بالإنترنت." : "No Internet Connection."}</p>
            </div>
          </div> : undefined
      }
    </>
  );
}

export default App;